import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createElementVNode as _createElementVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "page-shell" }
const _hoisted_2 = {
  key: 0,
  class: "fetching-state"
}
const _hoisted_3 = {
  key: 1,
  class: "error-text"
}
const _hoisted_4 = {
  key: 2,
  class: "info-note"
}
const _hoisted_5 = { class: "vendor-icon" }
const _hoisted_6 = { class: "vendor-copy" }
const _hoisted_7 = { class: "vendor-name-row" }
const _hoisted_8 = { class: "vendor-name" }
const _hoisted_9 = {
  key: 0,
  class: "vendor-rating"
}
const _hoisted_10 = {
  key: 1,
  class: "vendor-rating"
}
const _hoisted_11 = { class: "amount-value" }
const _hoisted_12 = { class: "section-name" }
const _hoisted_13 = { class: "quote-table-shell" }
const _hoisted_14 = { class: "quote-table" }
const _hoisted_15 = { class: "quote-col-item" }
const _hoisted_16 = { class: "quote-col-qty" }
const _hoisted_17 = { class: "quote-col-price" }
const _hoisted_18 = { class: "quote-col-total" }
const _hoisted_19 = { class: "section-total" }
const _hoisted_20 = { class: "quote-summary" }
const _hoisted_21 = { class: "summary-row" }
const _hoisted_22 = { class: "summary-value" }
const _hoisted_23 = { class: "summary-row" }
const _hoisted_24 = { class: "summary-value" }
const _hoisted_25 = { class: "summary-row" }
const _hoisted_26 = { class: "summary-value" }
const _hoisted_27 = { class: "summary-row" }
const _hoisted_28 = { class: "summary-value" }
const _hoisted_29 = { class: "text-right" }
const _hoisted_30 = { class: "detail-value" }

import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import LoadingState from "@/shared/components/feedback/LoadingState.vue";
import { fetchVendorQuotationDetail, type QuotationDetailResponse } from "@/shared/services/quotations.api";
import AppCard from "@/shared/components/ui/AppCard.vue";
import AppSectionHeader from "@/shared/components/ui/AppSectionHeader.vue";
import { formatCurrency, getStatusColor } from "@/shared/utils/utils";
import QuotationConvertAction from "@/modules/vendor/components/quotation-detail/QuotationConvertAction.vue";

type QuoteTotals = {
  sub_total: number;
  tax_amount: number;
  online_fee: number;
  total_amount: number;
};
type QuoteSectionItem = {
  name: string;
  quantity: number;
  price: number;
  total: number;
};
type QuoteSection = {
  title: string;
  items: QuoteSectionItem[];
  total: number;
};

export default /*@__PURE__*/_defineComponent({
  __name: 'QuotationDetailPage',
  setup(__props) {

const quotedOn = computed(() =>
  quotationData.value?.created_at ?? "—"
);

const route = useRoute();
const isLoading = ref(false);
const errorMessage = ref("");
const quotationData = ref<QuotationDetailResponse | null>(null);

const quotationId = computed(() => String(route.params.id ?? quotationData.value?.id ?? "").trim());

function toNumber(value: unknown, fallback = 0) {
  if (typeof value === "number") return Number.isFinite(value) ? value : fallback;
  const parsed = Number.parseFloat(String(value ?? ""));
  return Number.isFinite(parsed) ? parsed : fallback;
}

const quoteSections = computed<QuoteSection[]>(() => {
  const sections = quotationData.value?.sections ?? [];
  return sections.map((section, sectionIndex) => {
    const items: QuoteSectionItem[] = (section.items ?? []).map((item, itemIndex) => {
      const quantity = Math.max(toNumber(item.qty ?? 1, 1), 1);
      const price = toNumber(item.price ?? 0, 0);
      const total = toNumber(item.line_total ?? 0, quantity * price);
      return {
        name: String(item.title ?? `Item ${itemIndex + 1}`),
        quantity,
        price,
        total,
      };
    });

    return {
      title: String(section.title ?? `Section ${sectionIndex + 1}`),
      items,
      total: items.reduce((sum, item) => sum + item.total, 0),
    };
  });
});

const totalInfo = computed<QuoteTotals>(() => {
  const info = quotationData.value?.total_info ?? null;
  return {
    sub_total: toNumber(info?.sub_total, 0),
    tax_amount: toNumber(info?.tax_amount, 0),
    online_fee: toNumber((info as any)?.online_fee, 0),
    total_amount: toNumber(info?.total_amount, 0),
  };
});

const totalSummary = computed(() => ({
  subTotal: totalInfo.value.sub_total,
  taxAmount: totalInfo.value.tax_amount,
  onlineFee: totalInfo.value.online_fee,
  totalAmount: totalInfo.value.total_amount,
}));

const clientRecord = computed(() => {
  return quotationData.value?.client ?? null;
});

const clientInfo = computed(() => ({
  name: clientRecord.value?.name ?? "Client",
  email: clientRecord.value?.email ?? "",
  phone: clientRecord.value?.phone_no ?? "",
}));

const quotationStatus = computed(() =>
  quotationData.value?.status ?? "new"
);

async function loadQuotationDetail() {
  const id = String(route.params.id ?? "").trim();
  if (!id) {
    quotationData.value = null;
    errorMessage.value = "Quotation not found.";
    return;
  }

  isLoading.value = true;
  errorMessage.value = "";

  try {
    const response = await fetchVendorQuotationDetail(id);
    quotationData.value = response ?? null;
    if (!quotationData.value) errorMessage.value = "Quotation details are unavailable.";
  } catch (error) {
    quotationData.value = null;
    errorMessage.value = "Unable to load quotation details.";
  } finally {
    isLoading.value = false;
  }
}

watch(
  () => route.params.id,
  () => {
    loadQuotationDetail();
  },
);

onMounted(loadQuotationDetail);

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_divider = _resolveComponent("v-divider")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      (isLoading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            _createVNode(LoadingState, {
              loading: isLoading.value,
              message: "Loading quotation details...",
              "spinner-size": 24,
              "spinner-width": 3
            }, null, 8, ["loading"])
          ]))
        : (errorMessage.value)
          ? (_openBlock(), _createElementBlock("p", _hoisted_3, _toDisplayString(errorMessage.value), 1))
          : (!quotationData.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                _createVNode(_component_v_icon, {
                  size: "18",
                  class: "info-note-icon"
                }, {
                  default: _withCtx(() => [...(_cache[0] || (_cache[0] = [
                    _createTextVNode("mdi-information-outline", -1)
                  ]))]),
                  _: 1
                }),
                _cache[1] || (_cache[1] = _createElementVNode("p", { class: "info-note-text" }, "Quotation details are unavailable.", -1))
              ]))
            : (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                _createVNode(AppCard, { class: "vendor-card" }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_5, [
                      _createVNode(_component_v_icon, { size: "18" }, {
                        default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                          _createTextVNode("mdi-account-outline", -1)
                        ]))]),
                        _: 1
                      })
                    ]),
                    _createElementVNode("div", _hoisted_6, [
                      _createElementVNode("div", _hoisted_7, [
                        _createElementVNode("div", null, [
                          _createElementVNode("p", _hoisted_8, _toDisplayString(clientInfo.value.name), 1),
                          (clientInfo.value.phone)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                                _createVNode(_component_v_icon, {
                                  size: "14",
                                  class: "meta-icon"
                                }, {
                                  default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                                    _createTextVNode("mdi-phone-outline", -1)
                                  ]))]),
                                  _: 1
                                }),
                                _createElementVNode("span", null, _toDisplayString(clientInfo.value.phone), 1)
                              ]))
                            : _createCommentVNode("", true),
                          (clientInfo.value.email)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                                _createVNode(_component_v_icon, {
                                  size: "14",
                                  class: "meta-icon"
                                }, {
                                  default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                                    _createTextVNode("mdi-email-outline", -1)
                                  ]))]),
                                  _: 1
                                }),
                                _createElementVNode("span", null, _toDisplayString(clientInfo.value.email), 1)
                              ]))
                            : _createCommentVNode("", true)
                        ]),
                        _createElementVNode("div", null, [
                          _createVNode(_component_v_chip, {
                            label: "",
                            size: "small",
                            class: "text-capitalize",
                            color: _unref(getStatusColor)(quotationStatus.value)
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(quotationStatus.value), 1)
                            ]),
                            _: 1
                          }, 8, ["color"])
                        ])
                      ])
                    ])
                  ]),
                  _: 1
                }),
                _createVNode(AppCard, { class: "amount-card" }, {
                  default: _withCtx(() => [
                    _cache[5] || (_cache[5] = _createElementVNode("p", { class: "amount-title" }, "Quoted Amount", -1)),
                    _createElementVNode("p", _hoisted_11, _toDisplayString(_unref(formatCurrency)(totalSummary.value.totalAmount)), 1)
                  ]),
                  _: 1
                }),
                _createVNode(AppCard, { class: "detail-card" }, {
                  default: _withCtx(() => [
                    _createVNode(AppSectionHeader, { title: "Quotation Detail" }),
                    _createVNode(_component_v_divider),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(quoteSections.value, (section) => {
                      return (_openBlock(), _createElementBlock("div", {
                        key: section.title,
                        class: "quote-section"
                      }, [
                        _createElementVNode("p", _hoisted_12, _toDisplayString(section.title), 1),
                        _createElementVNode("div", _hoisted_13, [
                          _createElementVNode("table", _hoisted_14, [
                            _cache[7] || (_cache[7] = _createElementVNode("thead", null, [
                              _createElementVNode("tr", null, [
                                _createElementVNode("th", { class: "quote-col-item" }, "Item"),
                                _createElementVNode("th", { class: "quote-col-qty" }, "Qty"),
                                _createElementVNode("th", { class: "quote-col-price" }, "Price"),
                                _createElementVNode("th", { class: "quote-col-total" }, "Total")
                              ])
                            ], -1)),
                            _createElementVNode("tbody", null, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(section.items, (item) => {
                                return (_openBlock(), _createElementBlock("tr", {
                                  key: `${section.title}-${item.name}`
                                }, [
                                  _createElementVNode("td", _hoisted_15, _toDisplayString(item.name), 1),
                                  _createElementVNode("td", _hoisted_16, _toDisplayString(item.quantity), 1),
                                  _createElementVNode("td", _hoisted_17, _toDisplayString(_unref(formatCurrency)(item.price)), 1),
                                  _createElementVNode("td", _hoisted_18, _toDisplayString(_unref(formatCurrency)(item.total)), 1)
                                ]))
                              }), 128))
                            ]),
                            _createElementVNode("tfoot", null, [
                              _createElementVNode("tr", null, [
                                _cache[6] || (_cache[6] = _createElementVNode("td", {
                                  class: "section-total-label",
                                  colspan: "3"
                                }, "Section Total", -1)),
                                _createElementVNode("td", _hoisted_19, _toDisplayString(_unref(formatCurrency)(section.total)), 1)
                              ])
                            ])
                          ])
                        ])
                      ]))
                    }), 128))
                  ]),
                  _: 1
                }),
                _createElementVNode("table", _hoisted_20, [
                  _createElementVNode("tbody", null, [
                    _createElementVNode("tr", _hoisted_21, [
                      _cache[8] || (_cache[8] = _createElementVNode("td", null, "Sub Total", -1)),
                      _createElementVNode("td", _hoisted_22, _toDisplayString(_unref(formatCurrency)(totalSummary.value.subTotal)), 1)
                    ]),
                    _createElementVNode("tr", _hoisted_23, [
                      _cache[9] || (_cache[9] = _createElementVNode("td", null, "Tax", -1)),
                      _createElementVNode("td", _hoisted_24, _toDisplayString(_unref(formatCurrency)(totalSummary.value.taxAmount)), 1)
                    ]),
                    _createElementVNode("tr", _hoisted_25, [
                      _cache[10] || (_cache[10] = _createElementVNode("td", null, "Online Fee", -1)),
                      _createElementVNode("td", _hoisted_26, _toDisplayString(_unref(formatCurrency)(totalSummary.value.onlineFee)), 1)
                    ]),
                    _createElementVNode("tr", _hoisted_27, [
                      _cache[11] || (_cache[11] = _createElementVNode("td", null, "Total", -1)),
                      _createElementVNode("td", _hoisted_28, _toDisplayString(_unref(formatCurrency)(totalSummary.value.totalAmount)), 1)
                    ])
                  ])
                ]),
                _createElementVNode("div", _hoisted_29, [
                  _cache[12] || (_cache[12] = _createElementVNode("p", { class: "detail-label" }, "Quoted On", -1)),
                  _createElementVNode("p", _hoisted_30, _toDisplayString(quotedOn.value), 1)
                ])
              ], 64))
    ]),
    (quotationStatus.value == 'accepted')
      ? (_openBlock(), _createBlock(QuotationConvertAction, {
          key: 0,
          "quotation-id": quotationId.value,
          "on-success": loadQuotationDetail
        }, null, 8, ["quotation-id"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})